/**
 * Shared admin UI tokens — import as `import { AD } from "../adminUi"` (adjust path).
 */
export const AD = {
  page: "space-y-4 font-sans text-slate-900 antialiased dark:text-slate-100 sm:space-y-6",
  title: "text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl md:text-[1.65rem]",
  subtitle: "mt-0.5 text-xs text-slate-600 dark:text-slate-400 sm:text-sm",
  headerRow: "flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between",
  toolbar: "flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center",

  filterCard:
    "rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900/50 md:p-5",

  input:
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pl-9 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-100",

  select:
    "w-full cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm text-slate-900 shadow-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-100",

  btnPrimary:
    "inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 shadow-sm transition hover:bg-amber-300 active:scale-[0.99]",

  btnSecondary:
    "inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-amber-200 hover:bg-amber-50/60 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:hover:bg-gray-800",

  btnGhost:
    "inline-flex items-center justify-center rounded-xl border border-transparent px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-gray-800",

  btnDanger:
    "inline-flex items-center justify-center rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200",

  tableShell:
    "overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900",

  thead:
    "sticky top-0 z-10 bg-gradient-to-r from-amber-50 via-orange-50/40 to-amber-50/80 text-left text-xs font-bold uppercase tracking-wide text-slate-600 backdrop-blur-sm dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-800 dark:text-slate-300",

  statCard:
    "rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900",

  emptyState:
    "rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-14 text-center text-slate-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-slate-400",

  /** Long admin forms (coupon, banner) — narrow readable column */
  formWrap: "mx-auto min-w-0 w-full max-w-4xl space-y-6",
  /** Event create/edit — wider */
  formWrapWide: "mx-auto min-w-0 w-full max-w-full space-y-4 sm:max-w-6xl sm:space-y-6",

  /** Single card that wraps a whole form */
  formSurface:
    "rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6",

  /** Softer shell for very long forms */
  formSurfaceMuted:
    "rounded-2xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/90 p-5 shadow-sm dark:border-gray-800 dark:from-gray-900 dark:to-gray-950 sm:p-6",

  formHeader: "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between",

  fieldLabel: "mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200",

  fieldHint: "mt-1 text-xs text-slate-500 dark:text-slate-400",

  inputField:
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-70 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-100 dark:disabled:bg-gray-900",

  textareaField:
    "w-full min-h-[5.5rem] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-100",

  formGrid2: "grid grid-cols-1 gap-4 md:grid-cols-2",
  formGrid3: "grid grid-cols-1 gap-4 sm:grid-cols-3",

  toggleRow:
    "flex min-w-0 items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-gray-800 dark:bg-gray-900/50",

  dropZone:
    "rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/70 p-4 transition hover:border-amber-300 hover:bg-amber-50/35 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:border-amber-700 md:p-6",
};

/**
 * Scroll to and focus a form control marked with `data-admin-field="<key>"`.
 * Keys should be simple (e.g. `code`, `name`, `venue.city`, `ticketCategories`).
 * Non-focusable targets (e.g. a drop zone div) should use `tabIndex={-1}` on the same element.
 */
export function focusAdminField(fieldKey) {
  if (typeof document === "undefined" || fieldKey == null) return;
  const key = String(fieldKey).trim();
  if (!key) return;

  let el = null;
  try {
    el = document.querySelector(`[data-admin-field="${CSS.escape(key)}"]`);
  } catch {
    el = document.querySelector(`[data-admin-field="${key.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`);
  }
  if (!el) return;

  el.scrollIntoView({ behavior: "smooth", block: "center" });
  window.setTimeout(() => {
    try {
      if (typeof el.focus === "function") {
        el.focus({ preventScroll: true });
      }
    } catch {
      try {
        el.focus?.();
      } catch {
        /* ignore */
      }
    }
  }, 280);
}
